import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BacklogResponse,
  HelpRequestsResponse,
  JiraEpicsResponse,
  PersonWithTeams,
  ProductRequestsResponse,
  QualificationDetail,
  QualificationUpdate,
  QueueResponse,
  ReleaseNoteEditor,
  ReleaseNoteEditorIndex,
  ReleaseNoteSaveInput,
  RequestStatus,
  TeamMessageInput,
  TeamPrioritiesResponse,
  TeamPriorityFilter,
  VersionState,
} from '@product-portal/shared';
import { apiFetch } from '@/lib/api';
import { requestKeys } from './use-requests';

/**
 * Data of the product space (06 to 09, 13, 15). The API refuses every
 * /api/product route to anyone but a PO; these hooks only fetch and cache.
 */

export const productKeys = {
  all: ['product'] as const,
  queue: (params: object) => ['product', 'queue', params] as const,
  request: (id: string) => ['product', 'request', id] as const,
  people: (q: string) => ['product', 'people', q] as const,
  team: (params: object) => ['product', 'team-priorities', params] as const,
  backlog: (productId: string | null) => ['product', 'backlog', productId] as const,
  // Under the backlog prefix: whatever refreshes the backlog refreshes this section too.
  help: (productId: string) => ['product', 'backlog', productId, 'help'] as const,
  releaseNotes: ['product', 'release-notes'] as const,
  releaseNote: (versionId: string) => ['product', 'release-notes', versionId] as const,
};

const json = (body: unknown) => JSON.stringify(body);

export interface QueueParams {
  q: string;
  productId: string;
  type: string;
  status: string;
  page: number;
  pageSize: number;
}

export function useQueue(params: QueueParams) {
  const search = new URLSearchParams({ status: params.status, page: String(params.page), pageSize: String(params.pageSize) });
  if (params.q.trim()) search.set('q', params.q.trim());
  if (params.productId) search.set('productId', params.productId);
  if (params.type) search.set('type', params.type);
  return useQuery({
    queryKey: productKeys.queue(params),
    queryFn: () => apiFetch<QueueResponse>(`/api/product/queue?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

/** « Qualification » badge of the product header: requests waiting for qualification. */
export function useQueueCount(enabled: boolean) {
  return useQuery({
    queryKey: productKeys.queue({ badge: true }),
    queryFn: () => apiFetch<QueueResponse>('/api/product/queue?pageSize=6').then((r) => r.summary.toQualify),
    enabled,
    staleTime: 30_000,
  });
}

export function useQualification(id: string) {
  return useQuery({ queryKey: productKeys.request(id), queryFn: () => apiFetch<QualificationDetail>(`/api/product/requests/${id}`), retry: false });
}

/** Every write on a request refreshes the product space AND the business views of the request. */
function useRequestMutation<TVariables, TResult>(id: string, fn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: productKeys.all }), queryClient.invalidateQueries({ queryKey: requestKeys.all })]);
    },
  });
}

export function useQualify(id: string) {
  return useRequestMutation(id, (update: QualificationUpdate) =>
    apiFetch<{ changed: string[] }>(`/api/product/requests/${id}/qualification`, { method: 'PATCH', body: json(update) }),
  );
}

export function useTeamMessage(id: string) {
  return useRequestMutation(id, ({ input, files }: { input: TeamMessageInput; files: File[] }) => {
    const form = new FormData();
    form.append('data', JSON.stringify(input));
    for (const file of files) form.append('files', file, file.name);
    return apiFetch<{ id: string }>(`/api/product/requests/${id}/messages`, { method: 'POST', body: form });
  });
}

export function useRefuse(id: string) {
  return useRequestMutation(id, (reason: string) => apiFetch(`/api/product/requests/${id}/refusal`, { method: 'POST', body: json({ reason }) }));
}

export function useMarkDuplicate(id: string) {
  return useRequestMutation(id, (originalId: string) =>
    apiFetch<{ original: { id: string; reference: string } }>(`/api/product/requests/${id}/duplicate`, { method: 'POST', body: json({ originalId }) }),
  );
}

export function useLinkJira(id: string) {
  return useRequestMutation(id, (input: { mode: 'create'; epicKey?: string | null } | { mode: 'assign'; jiraKey: string }) =>
    apiFetch<{ jiraKey: string }>(`/api/product/requests/${id}/jira`, { method: 'POST', body: json(input) }),
  );
}

/** « Créer l'item » / « Affecter » on the product's Monday board (ADR-015). */
export function useLinkMonday(id: string) {
  return useRequestMutation(id, (input: { mode: 'create' } | { mode: 'assign'; itemId: string }) =>
    apiFetch<{ itemId: string }>(`/api/product/requests/${id}/monday`, { method: 'POST', body: json(input) }),
  );
}

/** Open epics of the board's project (« Créer le ticket », ADR-013); fetched only when asked. */
export function useJiraEpics(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['product', 'request', id, 'jira-epics'] as const,
    queryFn: () => apiFetch<JiraEpicsResponse>(`/api/product/requests/${id}/jira/epics`),
    enabled,
    staleTime: 60_000,
  });
}

export function usePeople(q: string) {
  return useQuery({
    queryKey: productKeys.people(q),
    queryFn: () => apiFetch<{ items: PersonWithTeams[] }>(`/api/product/people?${new URLSearchParams(q.trim() ? { q: q.trim() } : {}).toString()}`).then((r) => r.items),
    placeholderData: keepPreviousData,
  });
}

// ----- 06 · Priorisation par équipe ------------------------------------------

export function useTeamPriorities(params: { teamId: string | null; filter: TeamPriorityFilter; productId: string }) {
  const search = new URLSearchParams({ filter: params.filter });
  if (params.teamId) search.set('teamId', params.teamId);
  if (params.productId) search.set('productId', params.productId);
  return useQuery({
    queryKey: productKeys.team(params),
    queryFn: () => apiFetch<TeamPrioritiesResponse>(`/api/team-priorities?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function useSaveTeamPriorities(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestIds: string[]) => apiFetch(`/api/product/team-priorities/${teamId}`, { method: 'POST', body: json({ requestIds }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.all }),
  });
}

// ----- 09 · Backlog & arbitrage ----------------------------------------------

export function useBacklog(productId: string | null) {
  return useQuery({
    queryKey: productKeys.backlog(productId),
    queryFn: () => apiFetch<BacklogResponse>(`/api/product/backlog${productId ? `?productId=${productId}` : ''}`),
    placeholderData: keepPreviousData,
  });
}

/** « Aide & accès » section of screen 09 (ADR-011). */
export function useHelpRequests(productId: string) {
  return useQuery({
    queryKey: productKeys.help(productId),
    queryFn: () => apiFetch<HelpRequestsResponse>(`/api/product/backlog/${productId}/help`),
    enabled: productId !== '',
  });
}

/** « Voir tout » of screen 09: every request of the product. */
export function useProductRequests(productId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['product', 'backlog', productId, 'all'] as const,
    queryFn: () => apiFetch<ProductRequestsResponse>(`/api/product/backlog/${productId}/all`),
    enabled: enabled && productId !== '',
  });
}

export function useSaveDraftOrder(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestIds: string[]) => apiFetch(`/api/product/backlog/${productId}/draft-order`, { method: 'PUT', body: json({ requestIds }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function usePublishBacklog(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/api/product/backlog/${productId}/publication`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.all }),
  });
}

// ----- 13 · Release note -----------------------------------------------------

export function useReleaseNoteIndex() {
  return useQuery({ queryKey: productKeys.releaseNotes, queryFn: () => apiFetch<ReleaseNoteEditorIndex>('/api/product/release-notes') });
}

export function useReleaseNoteEditor(versionId: string | null) {
  return useQuery({
    queryKey: productKeys.releaseNote(versionId ?? ''),
    queryFn: () => apiFetch<ReleaseNoteEditor>(`/api/product/versions/${versionId}/release-note`),
    enabled: versionId !== null,
  });
}

export function useSaveReleaseNote(versionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReleaseNoteSaveInput) => apiFetch(`/api/product/versions/${versionId}/release-note`, { method: 'PUT', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: productKeys.releaseNotes }),
  });
}

export function usePublishReleaseNote(versionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ notifiedRequests: number }>(`/api/product/versions/${versionId}/release-note/publication`, { method: 'POST' }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: productKeys.releaseNotes }), queryClient.invalidateQueries({ queryKey: ['release-notes'] })]);
    },
  });
}

// Versions (ADR-012): they appear on 09 (current version), 08 (target version), 05 (upcoming) and 12 (current version).
function useVersionMutation<TVariables, TResult = unknown>(fn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: productKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['admin'] }),
        queryClient.invalidateQueries({ queryKey: ['release-notes'] }),
      ]),
  });
}

export function useCreateVersion() {
  return useVersionMutation(({ productId, number, plannedDate }: { productId: string; number: string; plannedDate: string | null }) =>
    apiFetch<{ id: string }>(`/api/product/products/${productId}/versions`, { method: 'POST', body: json({ number, plannedDate }) }),
  );
}

export function useUpdateVersion() {
  return useVersionMutation(({ id, ...input }: { id: string; state?: VersionState; plannedDate?: string | null; deliveredOn?: string | null }) =>
    apiFetch(`/api/product/versions/${id}`, { method: 'PATCH', body: json(input) }),
  );
}

/** « Demandes de la version » of screen 13 (2026-10-24). */
export function useAssociableRequests(versionId: string, q: string) {
  return useQuery({
    queryKey: ['product', 'associable', versionId, q.trim()] as const,
    queryFn: () =>
      apiFetch<{ items: { id: string; reference: string; title: string; teamName: string; status: RequestStatus }[] }>(
        `/api/product/versions/${versionId}/associable-requests?${new URLSearchParams({ q: q.trim() })}`,
      ).then((r) => r.items),
    enabled: versionId !== '' && q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function useAssociateRequest(versionId: string) {
  return useVersionMutation(({ requestId, associated }: { requestId: string; associated: boolean }) =>
    apiFetch(`/api/product/versions/${versionId}/requests/${requestId}`, { method: associated ? 'PUT' : 'DELETE' }),
  );
}

export function useDeleteVersion() {
  return useVersionMutation((id: string) => apiFetch(`/api/product/versions/${id}`, { method: 'DELETE' }));
}

/** Original of a duplicate, found by its reference (« PP-305 »). */
export function lookupRequest(reference: string) {
  return apiFetch<{ id: string; reference: string; title: string; status: string; teamName: string }>(
    `/api/product/requests/lookup?${new URLSearchParams({ reference }).toString()}`,
  );
}
